<template>
  <ts-popover class="ts-tag-picker" placement="bottom" :width="popoverWidth || width" trigger="manual" :value="open">
    <div slot="reference" ref="reference" class="tag-container">
      <ts-tag class="tag tag-add" v-for="tag in tagsVisible" :key="tag.id" @click="handleButtonClick">
        <span class="text">{{ tag.name }}</span>
        <span class="remove-tag" @click="handleRemoveExistingTag(tag.id, $event)"><XMark /></span>
      </ts-tag>
      <ts-popover v-if="tagsRest.length" placement="top" width="160" trigger="hover">
        <div class="tags-rest-label" slot="reference">+{{ tagsRest.length }}</div>
        <div>
          <p class="tags-rest-tag" v-for="tag in tagsRest" :key="tag.id">
            {{ tag.name }}
          </p>
        </div>
      </ts-popover>
      <ts-tag class="tag tag-add" :class="{ active: open }" @click="handleButtonClick">
        <PlusSmall />
      </ts-tag>
    </div>
    <template #header>
      <div class="adder">
        <div class="added-tags">
          <span v-for="tag in addedTags" :key="tag" class="tag">
            {{ tag }}<span class="remove-tag" @click="handleRemoveAddedTag(tag)"><XMark /></span
          ></span>
        </div>
        <div class="textbox">
          <PlusLight />
          <input type="text" placeholder="Enter new tag" v-model="addedTag" @keypress="handleAdderKeypress" />
        </div>
      </div>
    </template>
    <div :style="{ 'max-height': '300px', overflow: 'scroll' }">
      <div v-for="option in filteredOptions" :key="option.id" class="option">
        <ts-checkbox :value="checked[option.id]" @input="handleTagCheckboxChange(option.id, $event)">
          {{ option.name }}
        </ts-checkbox>
      </div>
    </div>
    <template #footer>
      <div class="actions">
        <ts-link @click="handleResetClick">Reset</ts-link>
        <ts-link type="primary" @click="handleSaveClick">Save</ts-link>
      </div>
    </template>
  </ts-popover>
</template>

<style lang="scss" scoped>
.ts-tag-picker::v-deep {
  .ts-button {
    text-align: left;
    display: flex;

    > span {
      flex-grow: 1;
      display: inline-flex;
      align-items: center;
      justify-content: space-between;
    }

    &:focus,
    &.active {
      border: solid 2px #99ffd1;
    }
  }

  .active .ts-button {
    border: solid 2px #99ffd1;
    box-shadow: -16px -16px 32px 0 #fafbff, 16px 16px 32px 0 #a6abbd;
  }
}

.tag-container {
  .remove-tag {
    margin-left: 4px;
    visibility: hidden;
  }

  &:hover {
    .remove-tag {
      visibility: visible;
    }
  }

  .tags-rest-label {
    display: inline-block;
    margin: 0 4px;

    font-family: Roboto;
    font-size: 12px;
    color: #6d7489;
  }
}

.tags-rest-tag {
  font-family: Roboto;
  font-size: 10px;
  text-align: center;
  color: #6d7587;

  margin: 4px 0;
}

.tag {
  outline: none;
}

.option {
  margin: 8px;
}

.actions {
  flex-grow: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 12px;
}

.adder {
  display: flex;
  flex-direction: column;
  padding: 12px 0 0 12px;

  .added-tags {
    .tag {
      border-radius: 8px;
      box-shadow: inset 2px 2px 6px 0 #a6abbd, inset -2px -2px 6px 0 #fafbff;
      border: solid 2px rgba(255, 255, 255, 0.4);
      background-blend-mode: soft-light, normal;
      background-image: linear-gradient(289deg, #000000, #ffffff -1%), linear-gradient(to bottom, #ebecf0, #ebecf0);

      padding: 2px 10px;

      font-family: Roboto;
      font-size: 12px;
      color: #6d7588;
    }

    .remove-tag {
      margin-left: 4px;
      cursor: pointer;
    }
  }

  .textbox {
    display: flex;
    align-items: center;
  }

  input {
    font-family: Roboto;
    font-size: 12px;
    line-height: 18px;
    color: #242424;

    width: 100%;
    padding: 8px;
    outline: none;

    &::placeholder {
      color: #9aa5b7;
    }
  }
}
</style>

<script>
import PlusSmall from '@/assets/plus-small.svg';
import PlusLight from '@/assets/plus-light.svg';
import XMark from '@/assets/x-mark.svg';

export default {
  components: {
    PlusSmall,
    PlusLight,
    XMark
  },
  props: {
    width: {
      type: Number,
      default: 100
    },
    popoverWidth: {
      type: Number,
      default: 0
    },
    placeholder: {
      type: String,
      default: null
    },
    options: {
      type: Array,
      default: () => []
    },
    value: {
      type: Array,
      default: () => []
    },
    tags: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      open: false,
      checked: {},
      addedTag: '',
      addedTags: []
    };
  },
  computed: {
    tagsVisible() {
      return this.tags.slice(0, 2);
    },
    tagsRest() {
      return this.tags.slice(2);
    },
    filteredOptions() {
      if (!this.addedTag) {
        return this.options;
      }
      return this.options.filter(option => option.name.toLowerCase().includes(this.addedTag.toLowerCase()));
    }
  },
  methods: {
    handleButtonClick() {
      Object.keys(this.checked).forEach(key => (this.checked[key] = false));
      this.value.forEach(v => (this.checked[v.id] = true));
      this.open = !this.open;
      this.addedTag = '';
    },
    handleDocumentClick(event) {
      if (this.open && !this.$refs.reference.contains(event.target)) {
        this.handleSaveClick();
      }
    },
    handleAdderKeypress(event) {
      if (event.code === 'Enter' && this.addedTag) {
        this.addedTags.push(this.addedTag);
        this.addedTag = '';
        this.handleSaveClick();
      }
    },
    handleTagCheckboxChange(optionId, value) {
      this.checked = {
        ...this.checked,
        [optionId]: value
      };
    },
    handleRemoveAddedTag(tag) {
      this.addedTags = this.addedTags.filter(t => t !== tag);
    },
    handleRemoveExistingTag(tagId, e) {
      e.stopPropagation();
      const tags = this.tags.filter(t => t.id !== tagId);
      this.$emit('input', { added: [], checked: tags });
    },
    handleResetClick() {
      this.open = false;
      this.$emit('input', { added: [], checked: [] });
    },
    handleSaveClick() {
      if (this.addedTag) {
        this.addedTags.push(this.addedTag);
        this.addedTag = '';
      }
      this.open = false;
      const checked = this.options.filter(o => this.checked[o.id]);
      this.$emit('input', { added: this.addedTags, checked });
      this.addedTags = [];
    }
  },
  mounted() {
    document.addEventListener('click', this.handleDocumentClick);
  },
  destroyed() {
    document.removeEventListener('click', this.handleDocumentClick);
  }
};
</script>
